<template>
  <v-card class="w-auto grow space-y-5 p-4" radius="lg">
    <!-- Vehicle Information Header -->
    <div class="d-flex flex-column flex-md-row align-center justify-space-between px-4">
      <div class="text-center text-md-left">
        <h2 class="text-h5 font-weight-bold text-grey-darken-3 mb-1">
          {{ getVehicleTitle(vin, vehicle) }}
        </h2>
        <div class="d-flex flex-column flex-md-row align-center gap-2 mt-2 my-2">
          <v-chip
            color="primary"
            variant="outlined"
            size="small"
            prepend-icon="mdi-bookmark"
            class="text-caption mr-2"
          >
            STK #{{ vehicle?.stockNumber }}
          </v-chip>
          <v-chip
            color="secondary"
            variant="outlined"
            size="small"
            prepend-icon="mdi-car"
            class="text-caption"
          >
            VIN: {{ vehicle?.vin }}
          </v-chip>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <v-tabs v-model="activeTab" :style="{ marginTop: 0, marginBottom: 0 }">
      <v-tab value="description">
        <div class="d-flex align-center">
          <span>Description</span>
          <v-chip
            v-if="descriptionStatusLabel"
            class="ml-2"
            size="x-small"
            label
            :color="getStatusColor(descriptionStatusLabel, 'description')"
            variant="tonal"
          >
            {{ descriptionStatusLabel }}
          </v-chip>
          <v-chip
            v-if="isDescriptionLocked && descriptionStatusLabel && descriptionStatusLabel.toLowerCase() !== 'missing'"
            class="ml-1"
            size="x-small"
            label
            color="green"
            variant="tonal"
          >
            AI-Protected
          </v-chip>
        </div>
      </v-tab>
      <v-tab value="highlights">
        <div class="d-flex align-center">
          <span>Highlights</span>
          <v-chip
            v-if="highlightStatusLabel"
            class="ml-2"
            size="x-small"
            label
            :color="getStatusColor(highlightStatusLabel, 'highlight')"
            variant="tonal"
          >
            {{ highlightStatusLabel }}
          </v-chip>
        </div>
      </v-tab>
    </v-tabs>

    <!-- Tab Content -->
    <v-tabs-window v-model="activeTab" class="mt-0">
      <!-- Description Tab -->
      <v-tabs-window-item value="description" :transition="false">
        <RichTextEditor
          ref="descriptionEditor"
          :content="tempDescription"
          :descriptions="descriptions"
          :enable-save="enableSave"
          :expand-editor="expandEditor"
          :vehicle="vehicle"
          :user-edited="userEdited"
          :saveChanges="saveChanges"
          :is-updating-content="isUpdatingContent"
          :source="source"
          :persisted-source="persistedSource"
          :isSaving="isSaving"
          :available-prompts="promptsStore.descriptionPrompts"
          content-type="description"
          default-format="Default"
          :has-edit-access="hasEditAccess"
          :descriptionLocked="isDescriptionLocked"
          :lockLoading="isTogglingLock"
          @set-user-edited="userEdited = $event"
          @set-temp-description="onAiUpdate($event)"
          @set-enable-save="toggleSave($event)"
          @set-updating-content="isUpdatingContent = $event"
          @set-source="updateSource($event)"
          @update="onEditorInput"
          @toggle-lock="toggleDescriptionLock"
        />
      </v-tabs-window-item>

      <!-- Highlights Tab -->
      <v-tabs-window-item value="highlights" :transition="false">
        <RichTextEditor
          ref="highlightsEditor"
          :content="tempHighlights"
          :descriptions="descriptions"
          :enable-save="enableSaveHighlights"
          :expand-editor="expandEditor"
          :vehicle="vehicle"
          :user-edited="userEditedHighlights"
          :saveChanges="saveHighlightChanges"
          :is-updating-content="isUpdatingHighlights"
          :source="highlightSource"
          :isSaving="isSavingHighlights"
          :available-prompts="promptsStore.highlightPrompts"
          content-type="highlights"
          default-format="BulletedLists"
          :has-edit-access="hasEditAccess"
          @set-user-edited="userEditedHighlights = $event"
          @set-temp-description="onHighlightUpdate($event)"
          @set-enable-save="toggleSaveHighlights($event)"
          @set-updating-content="isUpdatingHighlights = $event"
          @set-source="updateHighlightSource($event)"
          @update="onHighlightEditorInput"
        />
      </v-tabs-window-item>
    </v-tabs-window>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useVehicleStore } from '@/stores/vehicle'
import { usePromptsStore } from '@/stores/prompts'
import RichTextEditor from './editor/RichTextEditor.vue'
import { getEmptyDescriptionsTemplate, getNormalizedDescriptionSource, getStatusColor, getUserFriendlyDescriptionSource, getUserFriendlyHighlightSource } from '@/utils'
import { fetchVehicleDescription, updateVehicleDescription, setDescriptionLock } from '@/apis'
import { computeDescriptionSource, computeHighlightSource } from '@/contentSource'
import type { IDescription } from '@/types'

const accountInfo = useAccountSettingsStore()
const descriptionEditor = ref<any>(null)
const highlightsEditor = ref<any>(null)
const vehicleStore = useVehicleStore()
const promptsStore = usePromptsStore()

// Helper function to get vehicle title
const getVehicleTitle = (vin: string, vehicle: any) => {
  const title = `${vehicle?.year} ${vehicle?.make} ${vehicle?.model} ${vehicle?.trim}`.trim()
  return title || vin
}

// Tab state
const activeTab = ref('description')

// Description states
const expandEditor = ref(false)
const userEdited = ref(false)
const enableSave = ref(false)
const isUpdatingContent = ref(true)
const isSaving = ref(false)

// Highlights states
const userEditedHighlights = ref(false)
const enableSaveHighlights = ref(false)
const isUpdatingHighlights = ref(true)
const isSavingHighlights = ref(false)

const accountName = accountInfo.accountId
const vehicle = ref(vehicleStore.selectedVehicle)
const vin = computed(() => vehicle.value?.vin ?? '')

// Access control (strict role check)
const hasEditAccess = computed(() => {
  const role = String(accountInfo.role || '').toLowerCase()
  return (
    role === 'company_admin' ||
    role === 'company_owner' ||
    role === 'company_suadmin' ||
    role === 'client_admin'
  )
  // If managers should edit too, also allow: role === 'client_manager'
})

const descriptions = ref(getEmptyDescriptionsTemplate())
const tempDescription = ref('')
const tempHighlights = ref('')
const source = ref<string>(getNormalizedDescriptionSource(vehicle.value?.descriptionStatus ?? ''))
const highlightSource = ref<string>(
  getNormalizedDescriptionSource(vehicle.value?.highlightStatus ?? ''),
)
const persistedSource = computed(() => getNormalizedDescriptionSource(vehicle.value?.descriptionStatus ?? ''))

/**
 * Take provenance from the loaded record.
 *
 * A record that does not name a source leaves the label-derived value in place,
 * because a legacy vehicle genuinely has nothing better to offer. Anything the
 * record does name replaces it, whether or not the Admin recognizes the value:
 * an unrecognized source is omitted at save time, which is the point.
 */
const adoptRecordProvenance = (record?: IDescription) => {
  // `source` is the authoritative description key; `descriptionSource` is a
  // mirror only some write paths maintain, so it is the fallback rather than
  // the preference.
  const descriptionSource = record?.source ?? record?.descriptionSource
  if (descriptionSource) source.value = String(descriptionSource)
  if (record?.highlightSource) highlightSource.value = String(record.highlightSource)
}

// Description lock state (mirrors meta/default)
const isDescriptionLocked = ref<boolean>(!!vehicle.value?.descriptionLocked)
const isTogglingLock = ref<boolean>(false)
watch(
  () => vehicle.value,
  (v) => {
    isDescriptionLocked.value = !!v?.descriptionLocked
  },
  { immediate: true },
)

const toggleDescriptionLock = async () => {
  const accountId = accountName
  const v = vin.value
  const desired = !isDescriptionLocked.value
  try {
    isTogglingLock.value = true
    const ok = await setDescriptionLock(accountId, v, desired)
    if (ok) {
      isDescriptionLocked.value = desired
      if (vehicle.value) (vehicle.value as any).descriptionLocked = desired
    }
  } catch (e) {
    // no-op; UI remains unchanged on error
  } finally {
    isTogglingLock.value = false
  }
}

// Friendly status labels for chips (already user-friendly in table/store)
const descriptionStatusLabel = computed(() => vehicle.value?.descriptionStatus ?? '')
const highlightStatusLabel = computed(() => vehicle.value?.highlightStatus ?? '')

// Helper: normalize HTML to plain text for reliable comparisons
const normalizeToPlainText = (input: string): string => {
  if (!input || input === '<p></p>') return ''
  const div = document.createElement('div')
  div.innerHTML = input
  const text = (div.textContent || div.innerText || '') as string
  return text.replace(/\s+/g, ' ').trim()
}

// Prefer ai2 over default over a5 for both description and highlights
const pickBestDescriptionText = (descObj: any): string => descObj?.description ?? descObj?.value ?? ''
const pickBestHighlightText = (descObj: any): string => descObj?.highlight ?? ''

// Description methods
const onAiUpdate = (content: string) => {
  tempDescription.value = content
}

const toggleSave = (enabled: boolean) => {
  enableSave.value = enabled
}

const updateSource = (newSource: string) => {
  source.value = newSource
}

const onEditorInput = () => {
  userEdited.value = true
  enableSave.value = true
}

const saveChanges = async (description: string) => {
  isSaving.value = true

  try {
    const computedDescriptionSource = computeDescriptionSource(source.value)
    // Same empty semantics as the editor / highlight save (`''` / `<p></p>` / whitespace-only).
    const isEmptyDescription = normalizeToPlainText(description) === ''

    const updatePayload: any = {
      accountId: accountName || '',
      vin: vin.value,
      description,
    }

    // An empty description has no content to protect, so clear the lock atomically
    // with the save rather than leaving a locked empty description behind.
    if (isEmptyDescription) {
      updatePayload.descriptionLocked = false
    }

    // Omitted when the session source cannot be trusted, so the service decides
    // from the stored record rather than persisting a guess as provenance.
    if (computedDescriptionSource) {
      updatePayload.source = computedDescriptionSource
    }

    // Add prompt ID if a custom prompt is selected
    if (promptsStore.selectedDescriptionPromptId) {
      updatePayload.descriptionPrompt = promptsStore.selectedDescriptionPromptId
    }

    await updateVehicleDescription(updatePayload)

    // Update baseline so Revert disables
    descriptions.value.default.description = description

    // Only when the Admin made a claim; see the highlight save.
    if (vehicle.value && computedDescriptionSource) {
      (vehicle.value as any).descriptionStatus = getUserFriendlyDescriptionSource(computedDescriptionSource)
    }

    if (isEmptyDescription) {
      isDescriptionLocked.value = false
      if (vehicle.value) (vehicle.value as any).descriptionLocked = false
    } else if (
      computedDescriptionSource === 'ai2' ||
      computedDescriptionSource === 'user-provided'
    ) {
      // Auto-lock description when saved as AI-Customized or User-Customized
      try {
        const ok = await setDescriptionLock(accountName || '', vin.value, true)
        if (ok) {
          isDescriptionLocked.value = true
          if (vehicle.value) (vehicle.value as any).descriptionLocked = true
        }
      } catch {}
    }

    userEdited.value = false
    enableSave.value = false
    descriptionEditor.value?.resetBaseline?.()
  } catch (error) {
    console.error('Failed to save description:', error)
    throw error
  } finally {
    isSaving.value = false
  }
}

// Highlights methods (similar structure but for highlights)
const onHighlightUpdate = (content: string) => {
  tempHighlights.value = content
}

const toggleSaveHighlights = (enabled: boolean) => {
  enableSaveHighlights.value = enabled
}

const updateHighlightSource = (newSource: string) => {
  highlightSource.value = newSource
}

const onHighlightEditorInput = () => {
  userEditedHighlights.value = true
  enableSaveHighlights.value = true
}

const saveHighlightChanges = async (highlights: string) => {
  isSavingHighlights.value = true

  try {
    const computedHighlightSource = computeHighlightSource(
      highlightSource.value,
      normalizeToPlainText(highlights) === '',
    )

    const updatePayload: any = {
      accountId: accountName || '',
      vin: vin.value,
      highlight: highlights,
    }

    // Omitted when the session source cannot be trusted, which asks the service
    // to decide provenance from the stored record instead of taking a guess.
    if (computedHighlightSource) {
      updatePayload.source = computedHighlightSource
      updatePayload.highlightSource = computedHighlightSource
    }

    // Add prompt ID if a custom prompt is selected
    if (promptsStore.selectedHighlightPromptId) {
      updatePayload.highlightPrompt = promptsStore.selectedHighlightPromptId
    }

    await updateVehicleDescription(updatePayload)

    // Update baseline so Revert disables
    descriptions.value.default.highlight = highlights

    // Only when the Admin made a claim. Where it deferred to the service, the
    // decided provenance is not in the response, and showing a guess would be
    // worse than leaving the chip until the record is next loaded.
    if (vehicle.value && computedHighlightSource) {
      (vehicle.value as any).highlightStatus = getUserFriendlyHighlightSource(computedHighlightSource)
    }

    userEditedHighlights.value = false
    enableSaveHighlights.value = false
    highlightsEditor.value?.resetBaseline?.()
  } catch (error) {
    console.error('Failed to save highlights:', error)
    throw error
  } finally {
    isSavingHighlights.value = false
  }
}

// Load vehicle description data
const loadVehicleData = async () => {
  if (!vin.value || !accountName) return

  isUpdatingContent.value = true
  isUpdatingHighlights.value = true

  try {
    const data = await fetchVehicleDescription({
      accountId: accountName,
      vin: vin.value,
    })

    descriptions.value = data
    // Default-first behavior; accept either description or value to support test fixtures
    tempDescription.value = data.default?.description ?? data.default?.value ?? ''
    tempHighlights.value = data.default?.highlight ?? ''

    // The record is authoritative about provenance; the status chip was only a
    // stand-in until it arrived. Several sources render as the same label, so
    // reading the label back would flatten distinctions the backend made - a
    // translated description and an AI-written one are not interchangeable when
    // the next save has to say which one it is.
    adoptRecordProvenance(data.default)
  } catch (error) {
    console.error('Failed to load vehicle data:', error)
  } finally {
    isUpdatingContent.value = false
    isUpdatingHighlights.value = false
  }
}

// Watch for vehicle changes
watch(
  () => vin.value,
  () => {
    if (vin.value) {
      loadVehicleData()
    }
  },
  { immediate: true },
)

// Reset user edited state when switching tabs
watch(activeTab, () => {
  // Optional: You might want to warn users if they have unsaved changes
})
</script>

<style scoped>
.space-y-5 > * + * {
  margin-top: 1.25rem;
}
</style>
